<script setup lang="ts">
import type { PageFilter } from '@rialtic/types'
import VueDatePicker from '@vuepic/vue-datepicker'
import { computed, onMounted, ref, watch } from '#imports'
import { useFiltersState } from '../../composables/filters'
import { useUrlParamsState } from '../../composables/urlParamsState'
import '@vuepic/vue-datepicker/dist/main.css'
import type { ModelValue } from '@vuepic/vue-datepicker'
import { formatDisplayDate } from '../../composables/date'

const SERVICE_LABEL = 'Date of service'

const props = defineProps<{
  filter: PageFilter
}>()
const emit = defineEmits([
  'filter-all',
  'filter-change',
  'filter-clear',
  'error',
])

const { getUrlParams, setUrlParams } = useUrlParamsState()
const { clearAllFilters } = useFiltersState()

const dateRange = ref<ModelValue>()

const hasData = computed(
  () => Array.isArray(dateRange.value) && dateRange.value.length > 0,
)

const inputClasses = computed(() => {
  const base = `placeholder:[&&]:text-neutral-800 placeholder:[&&]:opacity-100
     [&&]:rounded-lg [&&]:text-xs [&&]:font-poppins [&&]:font-medium
     [&&]:text-neutral-800 [&&]:py-2 [&&]:px-3 [&&]:leading-4.5
     [&&]:w-35 text-ellipsis`

  if (hasData.value) {
    return `${base} [&&]:bg-secondary-50 [&&]:text-secondary-800 
            [&&]:border-transparent [&&]:w-57 [&&]:pr-10`
  }
  return base
})

/**
 * Custom formatter → puts the label inside the input
 * and hides the trailing dash unless a full range exists.
 */
const inputFormatter = (model: ModelValue) => {
  if (!Array.isArray(model)) return ''
  const [from, to] = model

  if (!from) return ''

  const start = formatDisplayDate(from as Date, 'MM/dd/yyyy')

  if (to instanceof Date) {
    const end = formatDisplayDate(to, 'MM/dd/yyyy')
    return `${SERVICE_LABEL}: ${start} – ${end}`
  }

  return `${SERVICE_LABEL}: ${start}`
}

const updateDateFilter = (value: ModelValue) => {
  emit('filter-change', { dateRange: value, name: props.filter.name })

  const urlRange = Array.isArray(value)
    ? value
        .filter((v) => v instanceof Date)
        .map((v) => formatDisplayDate(v as Date, 'yyyy-MM-dd'))
    : []

  if (urlRange.length) setUrlParams(props.filter.name, urlRange)
}

const { normalizedParams } = useUrlParamsState()
watch(normalizedParams, (newVal, oldVal) => {
  if (!Object.hasOwn(newVal, 'date_of_service_range')) {
    clearDateRange()
  }
})

const clearDateRange = () => {
  dateRange.value = []
  clearAllFilters(props.filter.name)
  emit('filter-clear', { name: props.filter.name })
}

const caretClass = computed(() => {
  return [
    'i-ph-caret-down-bold pointer-events-none absolute top-2.5 right-3 z-10',
    hasData.value ? 'text-secondary-800' : 'text-neutral-800',
  ]
})

onMounted(() => {
  dateRange.value = getUrlParams(props.filter.name).map((d) =>
    d.replace('-', '/'),
  )
})
</script>

<template>
  <div class="relative inline-block">
    <VueDatePicker
      v-model="dateRange"
      :clearable="false"
      :enable-time-picker="false"
      :format="inputFormatter"
      :placeholder="SERVICE_LABEL"
      :ui="{ input: inputClasses }"
      hide-input-icon
      partial-range
      range
      @cleared="clearAllFilters(filter.name)"
      @update:model-value="updateDateFilter"
    >
      <template #action-row="{ closePicker, selectDate }">
        <div class="flex w-full flex-col">
          <div class="w-full border-t border-neutral-200"></div>

          <div class="flex justify-end space-x-3 bg-white px-4 py-2">
            <button
              class="rounded-md px-3 py-1 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
              @click="closePicker()"
            >
              Cancel
            </button>

            <button
              v-if="hasData"
              class="rounded-md px-3 py-1 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
              @click="
                () => {
                  clearDateRange()
                  closePicker()
                }
              "
            >
              Clear
            </button>

            <button
              class="bg-primary-600 hover:bg-primary-700 rounded-md px-3 py-1 text-sm font-medium text-white"
              @click="selectDate()"
            >
              Select
            </button>
          </div>
        </div>
      </template>
    </VueDatePicker>

    <div :class="caretClass" />
  </div>
</template>
